import React, { useState } from "react";
import { Share2, Check } from "lucide-react";

/**
 * Per-station share button.
 * - On devices that support the native Web Share API (most phones), opens the
 *   OS share sheet (WhatsApp, X, IG DM, SMS, Mail, AirDrop, etc.).
 * - Everywhere else, copies a deep link to the clipboard and flashes a tick.
 *
 * Props:
 *   station  — required, must include {station_id, name, tagline?}
 *   compact  — boolean, renders as an icon-only pill (for StationCard)
 */
export default function ShareStationButton({ station, compact = false }) {
  const [copied, setCopied] = useState(false);
  if (!station?.station_id) return null;

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = `${origin}/s/${station.station_id}`;
  const title = `Tune in to ${station.name} on Fliboe`;
  const text = station.tagline
    ? `${station.name} — ${station.tagline}. Listen live on Fliboe:`
    : `${station.name} is live on Fliboe. Press play:`;

  const onClick = async (e) => {
    // Prevent parent <Link> / play handlers from firing
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (_) {
        // user dismissed — fall through to clipboard so they still have something usable
      }
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {}
  };

  if (compact) {
    return (
      <button
        onClick={onClick}
        aria-label={`Share ${station.name}`}
        title={copied ? "Link copied!" : "Share this station"}
        className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/60 border border-white/10 hover:bg-emerald-500 hover:text-black hover:border-emerald-500 text-white flex items-center justify-center transition backdrop-blur-md"
        data-testid={`station-share-${station.station_id}`}
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className="fl-btn ghost"
      data-testid={`station-share-${station.station_id}`}
    >
      {copied ? (
        <>
          <Check className="w-4 h-4 text-emerald-500" /> Link copied
        </>
      ) : (
        <>
          <Share2 className="w-4 h-4 text-emerald-500" /> Share station
        </>
      )}
    </button>
  );
}
